/**
 * useWlDunningSettings — leitura/escrita dos parâmetros de inadimplência do WL.
 *
 * Fase F (Contratação Uaizy v1): expõe `wl_get_dunning_settings` e
 * `wl_update_dunning_settings` para a aba Configurações > Inadimplência.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";

const log = createLogger("useWlDunningSettings");

export interface WlDunningSettings {
  block_after_days: number;
  suspend_after_days: number;
  default_new_tenant_plan_id: string | null;
  default_plan_name: string | null;
}

const QUERY_KEY = ["wl-dunning-settings"] as const;

export function useWlDunningSettings() {
  return useQuery({
    queryKey: QUERY_KEY,
    staleTime: 60_000,
    queryFn: async (): Promise<WlDunningSettings | null> => {
      // RPC SECURITY DEFINER — frontend não toca em wl_settings diretamente.
      const { data, error } = await supabase.rpc("wl_get_dunning_settings" as never);
      if (error) {
        log.error("Erro ao carregar parâmetros de inadimplência", { error });
        throw error;
      }
      const rows = (data ?? []) as Record<string, unknown>[];
      const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;

      if (!row) return null;
      return {
        block_after_days: Number(row.block_after_days) || 0,
        suspend_after_days: Number(row.suspend_after_days) || 0,
        default_new_tenant_plan_id: (row.default_new_tenant_plan_id as string | null) ?? null,
        default_plan_name: (row.default_plan_name as string | null) ?? null,
      };
    },
  });
}

export function useUpdateWlDunningSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { block_after_days: number; suspend_after_days: number }) => {
      const { error } = await supabase.rpc("wl_update_dunning_settings" as never, {
        p_block_after_days: input.block_after_days,
        p_suspend_after_days: input.suspend_after_days,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
